/**
 * Explainer.jsx
 *
 * A small "info" icon button that, on hover/click, reveals a wider popover
 * explaining how a number was calculated. Designed for KPI cards and section
 * headers where users (e.g. finance) want to see the underlying formula.
 *
 * Usage:
 *   <Explainer title="Total Pipeline" formula="SUM(quoted_price) where status = 'Open' and year=2026">
 *     This is the live value of bids you haven't won or lost yet.
 *   </Explainer>
 *
 * Renders a 14px info "?" badge that opens a wide rounded card with:
 *   - title (bold)
 *   - body paragraph (children)
 *   - formula (mono font, light bg)
 *
 * The card is drawn on document.body (a portal) at the "?"'s place on screen,
 * kept inside the window: inside a KPI box it used to be cut off by the
 * neighbouring boxes and the Dashboard widgets' rounded corners, and it took
 * the label's UPPERCASE letter spacing (10/12/26). Scrolling, resizing the
 * window, Escape or a click elsewhere closes it.
 */
import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

const WIDTH = 320;
const MARGIN = 8;

export function Explainer({ title, formula, children, align = 'right' }) {
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState(null); // { left, top } on screen, or null until measured
  const ref = useRef(null);
  const pop = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (pop.current && pop.current.contains(e.target)) return;
      setOpen(false);
    };
    const shut = () => setOpen(false);
    const key = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', key);
    window.addEventListener('resize', shut);
    // a scroll anywhere but inside the card itself (a long formula) closes it
    const scrolled = (e) => {
      if (!(pop.current && e.target instanceof Node && pop.current.contains(e.target))) shut();
    };
    document.addEventListener('scroll', scrolled, true);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', key);
      window.removeEventListener('resize', shut);
      document.removeEventListener('scroll', scrolled, true);
    };
  }, [open]);

  // place the card under the "?" (above it when there is no room below), inside the window
  useLayoutEffect(() => {
    if (!open || !ref.current) {
      setAt(null);
      return;
    }
    const b = ref.current.getBoundingClientRect();
    const vw = window.innerWidth || document.documentElement.clientWidth || 1024;
    const vh = window.innerHeight || document.documentElement.clientHeight || 768;
    const h = pop.current ? pop.current.offsetHeight : 0;
    let left = align === 'right' ? b.right - WIDTH : b.left;
    left = Math.max(MARGIN, Math.min(left, vw - WIDTH - MARGIN));
    let top = b.bottom + 8;
    if (h && top + h > vh - MARGIN && b.top - 8 - h >= MARGIN) top = b.top - 8 - h;
    setAt((was) => (was && was.left === left && was.top === top ? was : { left, top }));
  }, [open, align]);

  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-flex', verticalAlign: 'middle' }}>
      <button
        type="button"
        aria-label={`How is "${title}" calculated?`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        style={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          border: '1px solid #C8E6C9',
          background: open ? '#1B5E20' : '#F1F8E9',
          color: open ? '#fff' : '#1B5E20',
          fontSize: 10,
          fontWeight: 800,
          lineHeight: '14px',
          cursor: 'pointer',
          padding: 0,
          marginLeft: 6,
          fontFamily: 'Montserrat, sans-serif',
          transition: 'all 0.15s',
        }}
        title={`How is "${title}" calculated?`}
      >
        ?
      </button>
      {open &&
        createPortal(
          <div
            ref={pop}
            role="tooltip"
            style={{
              position: 'fixed',
              left: at ? at.left : -9999,
              top: at ? at.top : 0,
              visibility: at ? 'visible' : 'hidden',
              maxWidth: `calc(100vw - ${MARGIN * 2}px)`,
              boxSizing: 'border-box',
              textTransform: 'none',
              letterSpacing: 'normal',
              whiteSpace: 'normal',
              cursor: 'auto',
              background: '#fff',
              border: '1px solid #C8E6C9',
              borderRadius: 10,
              padding: 14,
              width: WIDTH,
              boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
              zIndex: 1000,
              fontFamily: "'Montserrat', sans-serif",
              textAlign: 'left',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{ fontSize: 11, fontWeight: 800, color: '#1B5E20', textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 6 }}
            >
              How "{title}" is calculated
            </div>
            {children && (
              <div style={{ fontSize: 11.5, color: '#333', lineHeight: 1.5, marginBottom: formula ? 10 : 0, fontWeight: 500 }}>
                {children}
              </div>
            )}
            {formula && (
              <div
                style={{
                  fontFamily: "'JetBrains Mono', 'Consolas', monospace",
                  fontSize: 10.5,
                  background: '#F1F8E9',
                  border: '1px solid #DCEDC8',
                  borderRadius: 6,
                  padding: '8px 10px',
                  color: '#1B5E20',
                  fontWeight: 600,
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {formula}
              </div>
            )}
          </div>,
          document.body
        )}
    </span>
  );
}
